import { useState } from "react";
import { ArrowLeft, XCircle, Pencil, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { Badge } from "@/shared/components/Badge";
import { estiloDeBoton } from "@/shared/lib/estilosDeBoton";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Almacen {
  id: string;
  nombre: string;
  responsable: string;
  descripcion: string;
  herramientas: number;
  proyecto: string;
  estado: "Activo" | "Inactivo";
}

interface Props {
  almacen: Almacen;
  onBack: () => void;
  onEdit: () => void;
  onDarDeBaja: (id: string) => Promise<void>;
}

// ─── Label + value pair ───────────────────────────────────────────────────────

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className="text-sm font-medium text-gray-800">{value}</p>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const AlmacenDetailView = ({ almacen, onBack, onEdit, onDarDeBaja }: Props) => {
  const [giving, setGiving] = useState(false);

  const handleDarDeBaja = async () => {
    if (giving) return;
    setGiving(true);
    try {
      await onDarDeBaja(almacen.id);
    } catch (err: unknown) {
      const e = err as { statusCode?: number; message?: string | string[] };
      const msg = e.statusCode === 500
        ? "Error del servidor. Inténtalo de nuevo."
        : Array.isArray(e.message) ? e.message.join(" ") : (e.message ?? "Error al dar de baja");
      toast.error(msg);
    } finally {
      setGiving(false);
    }
  };

  return (
    <div className="mx-auto px-6 py-8">

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onBack}
            className={estiloDeBoton("secundario")}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ver almacén</h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Dar de baja */}
          <button
            type="button"
            onClick={handleDarDeBaja}
            disabled={giving}
            className={estiloDeBoton("peligro")}
          >
            {giving
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <XCircle className="w-4 h-4" />
            }
            Dar de baja
          </button>

          {/* Editar */}
          <button
            type="button"
            onClick={onEdit}
            className={estiloDeBoton("primario")}
          >
            <Pencil className="w-4 h-4" />
            Editar
          </button>
        </div>
      </div>

      {/* Información general */}
      <div className="bg-white rounded-xl border shadow-sm" style={{ borderColor: "#E0E0E0" }}>
        <div className="px-6 py-4 border-b" style={{ borderColor: "#E0E0E0" }}>
          <h2 className="text-base font-semibold text-gray-800">Información general</h2>
        </div>
        <div className="p-6 space-y-5">
          {/* Row 1: Nombre | Responsable | Estatus */}
          <div className="grid grid-cols-3 gap-6">
            <Field label="Nombre"      value={almacen.nombre} />
            <Field label="Responsable" value={almacen.responsable} />
            <Field
              label="Estatus"
              value={
                <Badge color={almacen.estado === "Activo" ? "verde" : "gris"}>
                  {almacen.estado}
                </Badge>
              }
            />
          </div>

          {/* Row 2: Descripción */}
          <div>
            <p className="text-xs text-gray-500 mb-1">Descripción</p>
            <p className="text-sm text-gray-700 leading-relaxed">
              {almacen.descripcion || <span className="text-gray-400 italic">Sin descripción</span>}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlmacenDetailView;
